import { dateFormat } from "../../../../services/Utils/functionService";
import { useTranslation } from "react-i18next";
import PaperClip from "../../../../assets/icons/paperclip.svg";
import Return from "../../../../assets/icons/return.svg";
import { useCustomerRequestsStore } from "../../../../stores/Cac/Customer/customerRequests";
import useTooltip from "../../../../hooks/useTooltip";
import useSorting from "../../../../hooks/useSorting";
import message from "../../../../assets/icons/text-message.png";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCustomerMissionStore } from "../../../../stores/Cac/Customer/customerMission";

const CustomerRequestTable = () => {
  const { t } = useTranslation();
  // Store
  const {
    setRequestIdCustomer,
    setOpenDetailsCustomer,
    filteredRequests,
    setChoiceTab,
    displayAllRequests,
    setDisplayAllRequests,
  } = useCustomerRequestsStore();
  const { isRenewable } = useCustomerMissionStore();
  const navigate = useNavigate();
  // Hook
  const [tooltipState, handleMouseEnter, handleMouseLeave] = useTooltip();
  const [sortedData, handleSort] = useSorting(filteredRequests);
  
  // State pour le tri
  const [sortField, setSortField] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // Fonction de tri personnalisée
  const handleCustomSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    handleSort(field);
  };

  // Fonction pour obtenir l'icône de tri
  const getSortIcon = (field: string) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    
    if (sortDirection === "asc") {
      return (
        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      );
    } else {
      return (
        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      );
    }
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      {/* Filtres et tri */}
      <div className="hidden md:flex w-full flex-shrink-0 flex-col gap-3 border-b border-gray-200 bg-gray-50 p-3 lg:flex-row lg:items-center lg:justify-between sm:p-4">
        <div className="flex items-center gap-4">
          <button
            type="button"
            className="p-3 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors duration-200 group cursor-pointer"
            onClick={() => navigate(-1)}
            title={t("common.back")}
          >
            <img src={Return} alt="return" className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" />
          </button>
          {/* Boutons d'action */}
          {isRenewable && (
            <button
              type="button"
              className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 cursor-pointer"
              onClick={() => setDisplayAllRequests(!displayAllRequests)}
            >
              {displayAllRequests ? t("customerRequests.hideOld") : t("customerRequests.showOld")}
            </button>
          )}
        </div>
        <div className="flex w-full items-center gap-2 overflow-x-auto pb-1 lg:w-auto lg:pb-0">
          <span className="flex-shrink-0 text-sm font-medium text-gray-600">{t("common.sortBy")}</span>
          
          {/* Bouton de tri par ID */}
          <button 
            onClick={() => handleCustomSort("fakeId")}
            className={`flex flex-shrink-0 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all duration-200 sm:px-4 ${
              sortField === "fakeId" 
                ? "bg-blue-100 text-blue-700 border border-blue-200" 
                : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
            }`}
          >
            <span>{t("common.id")}</span>
            {getSortIcon("fakeId")}
          </button>
          
          {/* Bouton de tri par catégorie */}
          <button 
            onClick={() => handleCustomSort("CategoryDocRequest.name")}
            className={`flex flex-shrink-0 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all duration-200 sm:px-4 ${
              sortField === "CategoryDocRequest.name" 
                ? "bg-blue-100 text-blue-700 border border-blue-200" 
                : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
            }`}
          >
            <span>{t("common.category")}</span>
            {getSortIcon("CategoryDocRequest.name")}
          </button>
          
          {/* Bouton de tri par date de création */}
          <button 
            onClick={() => handleCustomSort("createdAt")}
            className={`flex flex-shrink-0 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all duration-200 sm:px-4 ${
              sortField === "createdAt" 
                ? "bg-blue-100 text-blue-700 border border-blue-200" 
                : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
            }`}
          >
            <span>{t("customerRequests.creation")}</span>
            {getSortIcon("createdAt")}
          </button>
          
          {/* Bouton de tri par date limite */}
          <button 
            onClick={() => handleCustomSort("dueDate")}
            className={`flex flex-shrink-0 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all duration-200 sm:px-4 ${
              sortField === "dueDate" 
                ? "bg-blue-100 text-blue-700 border border-blue-200" 
                : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
            }`}
          >
            <span>{t("customerRequests.date")}</span>
            {getSortIcon("dueDate")}
          </button>
          
          {/* Bouton de tri par statut */}
          <button 
            onClick={() => handleCustomSort("State.state")}
            className={`flex flex-shrink-0 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all duration-200 sm:px-4 ${
              sortField === "State.state" 
                ? "bg-blue-100 text-blue-700 border border-blue-200" 
                : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
            }`}
          >
            <span>{t("common.status")}</span>
            {getSortIcon("State.state")}
          </button>
        </div>
      </div>

      {/* En-têtes de colonnes */}
      <div className="min-h-0 w-full flex-1 scroll-pb-20 overflow-y-auto pb-20 sm:scroll-pb-3 sm:pb-3">
      <div className="sticky top-0 z-10 hidden w-full grid-cols-9 border-b border-gray-200 bg-gray-50 py-3 text-sm font-semibold text-gray-700 md:grid">
        <div className="col-start-1 col-end-2 flex justify-start items-center ml-2">
          {t("common.id")}
        </div>
        <div className="col-start-2 col-end-3 flex justify-start items-center">
          {t("common.category")}
        </div>
        <div className="hidden lg:flex col-start-3 col-end-4 justify-start items-center">
          {t("customerRequests.creation")}
        </div>
        <div className="hidden lg:flex col-start-4 col-end-5 justify-start items-center">
          {t("customerRequests.limit")}
        </div>
        <div className="hidden sm:flex sm:col-start-5 sm:col-end-8 justify-start items-center">
          {t("customerRequests.description")}
        </div>
        <div className="hidden sm:flex sm:col-start-8 sm:col-end-9 justify-start items-center">
          {t("customerRequests.requester")}
        </div>
        <div className="col-start-9 col-end-10 flex justify-start items-center">
          <div className="grid grid-cols-2 w-full">
            <div className="hidden lg:flex lg:col-start-1 lg:col-end-2 mr-2">
              {t("customerRequests.docs")}
            </div>
            <div className="col-start-1 lg:col-start-2 col-end-3 mr-2 flex items-center">
              {t("common.status")}
            </div>
          </div>
        </div>
      </div>

      {/* Corps de la table */}
      <div className="w-full">
        {sortedData
          ?.filter(
            (request) =>
              displayAllRequests ||
              request.isPeriodClosed === null ||
              request.isPeriodClosed === false
          )
          .map((request: any) => (
            <div
              key={request?.id}
              className="w-full hover:bg-blue-50 transition-all duration-200 border-b border-gray-100"
            >
              <button
                type="button"
                className="flex w-full cursor-pointer flex-col gap-3 p-4 text-left transition-colors duration-200 hover:bg-blue-50 md:grid md:min-h-16 md:grid-cols-9 md:items-center md:gap-0"
                onClick={() => {
                  setOpenDetailsCustomer(true);
                  setRequestIdCustomer(request?.id);
                  setChoiceTab("Documents");
                }}
              >
                {/* ID et indicateurs */}
                <div className="flex items-center gap-2 md:col-start-1 md:col-end-2">
                  <div className="font-medium text-gray-900">
                    {request?.fakeId}
                  </div>
                  <div className="flex items-center gap-1">
                    {request?.refused === true && (
                      <div
                        className="text-lg"
                        onMouseEnter={(e) => {
                          if (request?.refused) {
                            handleMouseEnter(t("customerRequests.refused"), e);
                          }
                        }}
                        onMouseLeave={
                          request?.refused ? handleMouseLeave : undefined
                        }
                      >
                        📌
                      </div>
                    )}
                    {request?.State?.id !== 1 && Object.keys(request?.reminder || {}).length > 0 && (
                      <div
                        className="text-lg"
                        onMouseEnter={(e) => {
                          if (Object.keys(request?.reminder || {}).length > 0) {
                            handleMouseEnter(
                              t("customerRequests.relaunched", {
                                date: dateFormat(request.reminder?.date),
                                user: request?.reminder?.user,
                              }),
                              e
                            );
                          }
                        }}
                        onMouseLeave={
                          request?.reminder ? handleMouseLeave : undefined
                        }
                      >
                        📩
                      </div>
                    )}
                    {request?.chatRead === false && (
                      <div className="rounded-full w-2 h-2 bg-red-600"></div>
                    )}
                    {request?.commentaryCount !== 0 && (
                      <div className="flex relative">
                        <img
                          src={message}
                          alt="message"
                          className="h-4 w-4"
                        />
                        <div className="rounded-full h-3 w-3 bg-gray-500 text-white text-xs flex justify-center items-center absolute -top-1 -right-2">
                          {request?.commentaryCount}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Catégorie */}
                <div className="flex items-center md:col-start-2 md:col-end-3">
                  <div className="text-gray-700 font-medium">
                    {request?.CategoryDocRequest?.name}
                  </div>
                </div>

                {/* Date de création */}
                <div className="hidden items-center lg:col-start-3 lg:col-end-4 lg:flex">
                  <div className="text-gray-600 text-sm">
                    {dateFormat(request?.createdAt)}
                  </div>
                </div>

                {/* Date limite */}
                <div className="hidden items-center lg:col-start-4 lg:col-end-5 lg:flex">
                  <div className="text-gray-600 text-sm">
                    {dateFormat(request?.dueDate)}
                  </div>
                </div>

                {/* Description */}
                <div className="flex items-center md:col-start-5 md:col-end-8 md:mr-2">
                  <div className="text-gray-700 text-sm line-clamp-2">
                    {request?.description}
                  </div>
                </div>

                {/* Demandeur */}
                <div className="flex items-center md:col-start-8 md:col-end-9">
                  <span className="mr-2 text-xs font-semibold text-gray-500 md:hidden">
                    {t("customerRequests.requester")}:
                  </span>
                  <div className="text-gray-600 text-sm">
                    {request?.User}
                  </div>
                </div>

                {/* Documents et Statut */}
                <div className="flex h-full items-center justify-between border-t border-gray-100 pt-3 md:col-start-9 md:col-end-10 md:border-t-0 md:pt-0">
                  <div className="flex items-center gap-2">
                    <span className="flex items-center gap-1 text-gray-600">
                      <img
                        src={PaperClip}
                        alt="paperclip"
                        className="w-4 h-4"
                      />
                      <span className="text-sm">{request?.documentCount}</span>
                    </span>
                  </div>
                  
                  <div className="w-20 flex justify-start items-center gap-2">
                    {/* Indicateur de statut */}
                    <div
                      className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                        request?.State?.id === 1
                          ? "border-yellow-400 bg-yellow-400"
                          : request?.State?.id === 2
                          ? "border-blue-400 bg-blue-400"
                          : request?.State?.id === 3
                          ? "border-green-400 bg-green-400"
                          : request?.State?.id === 4
                          ? "border-red-500 bg-red-500"
                          : "border-gray-400 bg-gray-400"
                      }`}
                    ></div>
                    <span className="text-sm text-gray-600 whitespace-nowrap">
                      {request?.State?.state}
                    </span>
                  </div>
                </div>
              </button>
            </div>
          ))}
      </div>
      </div>
      {/* Tooltip */}
      {tooltipState.content && (
        <div
          className="bg-gray-600 text-white p-2 rounded-md text-sm absolute z-50 w-auto max-w-[200px] h-auto"
          style={{
            top: tooltipState.position.y,
            left: tooltipState.position.x,
          }}
        >
          {tooltipState.content}
        </div>
      )}
    </div>
  );
};

export default CustomerRequestTable;
